/*
|-----------------------------------------------------------------------------
| Source-name handling
|-----------------------------------------------------------------------------
|
| Source names have two deliberately separate representations:
|
| 1. Display/API value
|    The exact canonical name returned by the source catalog is preserved.
|    This value may contain a Persian half-space (U+200C) and is the value sent
|    back to the API.
|
| 2. Lookup key
|    A compact comparison-only key ignores half-spaces, normal spaces,
|    Arabic/Persian Yeh/Kaf differences, and invisible direction markers.
|
| This prevents names such as "فیلم‌زی", "فیلم زی", and "فیلمزی" from being
| treated as different selections without rewriting every news item or running
| a source-merging pass across the feed.
|
*/

const SOURCE_KEY_CACHE_LIMIT = 256;
const sourceKeyCache = new Map();

/**
 * Clean a source name for display/storage/API use while preserving meaningful
 * characters such as the Persian half-space.
 */
export const normalizeSourceName = (value) => {
  if (value === undefined || value === null) return "";

  return String(value)
    .normalize("NFC")
    .replace(/[\u200d\u200e\u200f\ufeff]/g, "")
    .replace(/\u00a0/g, " ")
    .replace(/[\t\r\n\f\v ]+/g, " ")
    .trim();
};

/**
 * Build a comparison-only key. The original source value is never replaced by
 * this key and this function is never used as an API parameter.
 */
export const getSourceKey = (value) => {
  if (value === undefined || value === null) return "";

  const rawValue = String(value);
  const cachedKey = sourceKeyCache.get(rawValue);

  if (cachedKey !== undefined) {
    return cachedKey;
  }

  const key = rawValue
    .normalize("NFKC")
    .replace(/[يى]/g, "ی")
    .replace(/ك/g, "ک")
    .replace(/[\u200c\u200d\u200e\u200f\ufeff\u00a0\s]+/g, "")
    .toLocaleLowerCase("fa-IR");

  if (sourceKeyCache.size >= SOURCE_KEY_CACHE_LIMIT) {
    sourceKeyCache.clear();
  }

  sourceKeyCache.set(rawValue, key);

  return key;
};

export const isSameSource = (left, right) => {
  const leftKey = getSourceKey(left);
  const rightKey = getSourceKey(right);

  return Boolean(leftKey) && leftKey === rightKey;
};

/**
 * Remove duplicate source entries by lookup key while preserving the first
 * canonical display/API value exactly.
 */
export const normalizeSourceList = (sources = []) => {
  const normalized = [];
  const seen = new Set();

  (sources || []).forEach((source) => {
    const value = normalizeSourceName(source);
    const key = getSourceKey(value);

    if (!key || seen.has(key)) return;

    seen.add(key);
    normalized.push(value);
  });

  return normalized;
};

const persianSourceCollator = new Intl.Collator("fa-IR", {
  sensitivity: "base",
  ignorePunctuation: true,
  numeric: true,
});

export const sortSourceListAlphabetically = (sources = []) => {
  return normalizeSourceList(sources)
    .map((source, index) => ({
      source,
      index,
    }))
    .sort((left, right) => {
      const comparison = persianSourceCollator.compare(
        left.source,
        right.source
      );

      /* Preserve stable order when collated names compare equal. */
      return comparison || left.index - right.index;
    })
    .map(({ source }) => source);
};

/**
 * Kept for compatibility with older imports. News rows now retain the exact
 * source value returned by the API and no per-item source rewrite is needed.
 */
export const normalizeNewsSource = (item) => item;

export const getSourceMapValue = (map, source, fallback = undefined) => {
  if (!map || typeof map !== "object") return fallback;

  const key = getSourceKey(source);
  if (!key) return fallback;

  if (Object.prototype.hasOwnProperty.call(map, key)) {
    return map[key];
  }

  const rawSource = String(source ?? "");

  if (Object.prototype.hasOwnProperty.call(map, rawSource)) {
    return map[rawSource];
  }

  const matchingEntry = Object.entries(map).find(([entryKey]) => {
    return getSourceKey(entryKey) === key;
  });

  return matchingEntry ? matchingEntry[1] : fallback;
};
